import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import "./OTP.css";

const OTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(300); // 5 minutes in seconds
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const timerRef = useRef(null);
  
  // Get email from query params
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");

  // Format timer as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start the countdown timer
  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [timer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      Swal.fire({
        icon: 'error',
        title: 'Email Missing',
        text: 'Email is required for verification',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/sign-in/otp-verify`,
        { email, otp }
      );
      
      if (response.data.success) {
        Swal.fire({
          title: "OTP Verified!",
          text: "You can now reset your password",
          icon: "success",
        });
        navigate(`/reset-password?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      Swal.fire({
        title: "Verification Failed",
        text: error.response?.data?.message || "Invalid OTP or OTP expired",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResendDisabled(true);
    setTimer(300); // Reset timer to 5 minutes
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/sign-in/resend-otp`,
        { email }
      );
      
      if (response.data.success) {
        Swal.fire({
          title: "OTP Resent!",
          text: "A new OTP has been sent to your email",
          icon: "success",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Resend Failed",
        text: error.response?.data?.message || "Failed to resend OTP",
        icon: "error",
      });
      setIsResendDisabled(false);
    }
  };

  return (
    <div className="otp-verification-container">
      <div className="otp-verification-card">
        <div className="otp-verification-header">
          <div className="verification-icon">
            <LockOutlinedIcon style={{ fontSize: '3rem' }} />
          </div>
          <h1>Verify Your Email</h1>
          {email && <p>We've sent a 6-digit code to {email}</p>}
        </div>

        <form onSubmit={handleSubmit} className="otp-verification-form">
          <div className="input-group">
            <LockOutlinedIcon className="input-icon" />
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              disabled={isLoading}
              maxLength={6}
            />
          </div>

          <button type="submit" disabled={isLoading || !email} className="submit-button">
            {isLoading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <div className="otp-timer-container">
          <div className="otp-timer">
            <span>Time Remaining: </span>
            <span className="timer-count">{formatTime(timer)}</span>
          </div>
          
          <p>Didn't receive the code?</p>
          
          <button
            className={`resend-button ${isResendDisabled ? 'disabled' : ''}`}
            onClick={handleResendOTP}
            disabled={isResendDisabled}
          >
            Resend OTP
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTP;